import { useEffect, useState, type ReactNode } from 'react';
import {
  backfillEmbeddings,
  getLedger,
  getPipeline,
  getStats,
  getStuck,
  getSweep,
  isUnavailable,
  listCommissions,
  type CommissionRow,
  type LedgerRow,
  type PipelineSummary,
  type Stats,
  type StuckRow,
  type Sweep,
  type SweepFinding,
} from '../api';
import { message, settled, useAsync, type Settled } from '../lib/async';
import {
  Card,
  Failed,
  Id,
  Link,
  Pill,
  Refused,
  Result,
  Toolbar,
  money,
  span,
} from '../lib/ui';

/**
 * What to say under a number the pipeline could not give us.
 *
 * Every tile fed by /admin/pipeline needs the same sentence, because on this
 * deployment they all fail together and for the same permanent reason. Written
 * once so a screen cannot end up with one honest tile and one confident dash.
 */
const PIPELINE_DETAIL =
  'Films still run; watching one is what is missing. shared/workflow.py ' +
  'exposes no introspection on the Durable lane, so all four /admin/pipeline ' +
  'routes refuse up front rather than answering with a zero.';

/**
 * What a tile says under a "?" when its source did not answer.
 *
 * A card note is a CAPTION — one grey line under a number, styled at 11px, and
 * six of them sit side by side. It used to be handed `sources.pipeline.error`
 * raw, which is the server's whole refusal: on this deployment that is a
 * 400-character paragraph naming Durable Functions, the sweep route and the
 * fifteen-minute schedule. THREE of these six tiles are fed by /admin/pipeline
 * and they all fail together, so the top of the console — the six numbers an
 * operator scans at 3am — rendered the same essay three times, blew the row to
 * roughly 400px tall, and pushed "Needs a human" off the first screen entirely.
 * It has been the permanent state of the deployed portal.
 *
 * The server's sentence is not lost and is not paraphrased: `Failed` prints it
 * verbatim, once, in "Needs a human" below, which is where an operator reads
 * prose. It is also on the tile's own tooltip. What the tile gets is the short
 * fact plus which of the two things happened, because they need different
 * reactions — a permanent refusal is not an incident, and a failed call is.
 */
function tileNote(source: { ok: boolean; error?: string; cause?: unknown }): ReactNode {
  if (source.ok) return '';
  const full = source.error ?? '';
  if (isUnavailable(source.cause)) {
    return (
      <span title={full}>
        not available on this deployment — the reason is under “Needs a human”
      </span>
    );
  }
  // A genuine failure, so the words matter — but a tile cannot hold a stack of
  // them. First sentence, then the tooltip.
  const first = full.split(' — ')[0] ?? full;
  return <span title={full}>the call failed: {first}</span>;
}

/**
 * The first screen, and the only one an operator should need to open to know
 * whether anything is wrong.
 *
 * Five questions, in the order they get asked at 3am: what is running right now
 * and for whom, what failed recently, what is waiting on a human, what has been
 * spent today, and is anything red.
 *
 * Two design decisions do most of the work here.
 *
 * **Every source is settled, not awaited together.** Six endpoints feed this
 * page and several of them are scans. `Promise.all` would blank the entire first
 * screen because one of them is slow or newly deployed, which is exactly the
 * moment a console has to keep working. Each tile therefore reports its own
 * failure in its own note, and the tiles around it stay true.
 *
 * **A number that could not be fetched is never rendered as zero.** The pipeline
 * handler is careful about this — it returns `running: null` with an
 * `executionsError` rather than `0` when the orchestrator will not answer — and
 * throwing that away in the UI would re-introduce the exact failure it avoided:
 * an operator reading "0 running" and concluding the pipeline is idle while it
 * is on fire.
 */

/** The cheap poll. `?source=executions` asks only for runs in flight — no
 *  database read at all, ~0.04 s — which is what makes a live tick affordable. The
 *  full summary scans showling-commissions and is deliberately not polled. */
const LIVE_POLL_MS = 15_000;

export function Dashboard() {
  const [live, setLive] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => setTick((n) => n + 1), LIVE_POLL_MS);
    return () => clearInterval(timer);
  }, [live]);

  const { result, reload } = useAsync(async () => {
    const [pipeline, stuck, sweep, failed, awaiting, ledger, stats] = await Promise.all([
      settled(getPipeline()),
      settled(getStuck()),
      // The fallback for `stuck`, and on this deployment the only one of the
      // two that can answer. Fetched unconditionally rather than after `stuck`
      // fails, because chaining them would put a second round trip in front of
      // the section an operator opens during an incident — and because `stuck`
      // refuses instantly, so there is nothing to wait for.
      settled(getSweep()),
      settled(listCommissions({ status: 'failed', limit: 200 })),
      settled(listCommissions({ status: 'awaiting_approval', limit: 200 })),
      settled(getLedger({ limit: 500 })),
      settled(getStats()),
    ]);
    return { pipeline, stuck, sweep, failed, awaiting, ledger, stats };
  }, [tick]);

  return (
    <>
      <Toolbar>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>Right now</h2>
        <span className="spacer" />
        <label>
          <input
            type="checkbox"
            checked={live}
            onChange={(e) => setLive(e.target.checked)}
          />{' '}
          live ({LIVE_POLL_MS / 1000}s)
        </label>
        <button onClick={reload}>Refresh</button>
      </Toolbar>

      <Result of={result} what="the overview" retry={reload}>
        {(sources) => <Panels sources={sources} onFixed={reload} />}
      </Result>
    </>
  );
}

type Sources = {
  pipeline: Settled<PipelineSummary>;
  stuck: Settled<{ stuck: StuckRow[]; byKind?: Record<string, number>; executionsError?: string | null }>;
  sweep: Settled<Sweep>;
  failed: Settled<{ rows: CommissionRow[]; next?: string }>;
  awaiting: Settled<{ rows: CommissionRow[]; next?: string }>;
  ledger: Settled<{ entries: LedgerRow[] }>;
  stats: Settled<Stats>;
};

function Panels({ sources, onFixed }: { sources: Sources; onFixed: () => void }) {
  const pipeline = sources.pipeline.ok ? sources.pipeline.value : null;
  const stuck = sources.stuck.ok ? sources.stuck.value.stuck : null;
  const stats = sources.stats.ok ? sources.stats.value : null;

  const failedRecently = sources.failed.ok
    ? sources.failed.value.rows.filter((row) => withinDays(row.createdAt, 7))
    : null;
  const awaiting = sources.awaiting.ok ? sources.awaiting.value.rows : null;
  const spend = sources.ledger.ok ? spentToday(sources.ledger.value.entries) : null;

  return (
    <>
      <div className="cards">
        <Card
          label="Executions running"
          value={pipeline?.running ?? <span className="neg">?</span>}
          // Unknown is red, zero is not. They are different facts and the whole
          // reason the handler returns null rather than 0.
          {...(pipeline?.running == null ? { tone: 'bad' as const } : {})}
          note={
            !sources.pipeline.ok
              ? tileNote(sources.pipeline)
              : pipeline?.executionsError
                ? `the orchestrator would not answer: ${pipeline.executionsError}`
                : 'live from the orchestrator: runs in flight right now'
          }
        />
        <Card
          label="Peeks in flight"
          value={
            sources.pipeline.ok ? (
              fallback(pipeline?.commissions?.previewing)
            ) : (
              <span className="neg">?</span>
            )
          }
          {...(sources.pipeline.ok ? {} : { tone: 'bad' as const })}
          note={
            sources.pipeline.ok
              ? 'free previews — dispatched directly, never an execution'
              : tileNote(sources.pipeline)
          }
        />
        <Card
          label="Awaiting approval"
          value={awaiting === null ? <span className="neg">?</span> : awaiting.length}
          tone={awaiting === null ? 'bad' : awaiting.length ? 'warn' : undefined}
          note={
            sources.awaiting.ok
              ? 'a parent has to press play on these'
              : tileNote(sources.awaiting)
          }
        />
        <Card
          label="Failed, last 7 days"
          value={
            failedRecently === null ? <span className="neg">?</span> : failedRecently.length
          }
          tone={failedRecently === null ? 'bad' : failedRecently.length ? 'bad' : undefined}
          note={
            sources.failed.ok
              ? 'commission status — NOT execution status; they disagree'
              : tileNote(sources.failed)
          }
        />
        {/* This tile used to render "—" under the caption "owed a video and not
            yet released" whenever /admin/pipeline failed: a dash that reads as
            a number, under a sentence that sounds like a measurement. It is the
            empty-table lie in miniature, and on this deployment it was the
            permanent state of the tile. Unknown is "?" and it is red. */}
        <Card
          label="Credits held"
          value={
            sources.pipeline.ok ? (
              fallback(pipeline?.commissions?.creditsHeld)
            ) : (
              <span className="neg">?</span>
            )
          }
          {...(sources.pipeline.ok ? {} : { tone: 'bad' as const })}
          note={
            sources.pipeline.ok ? 'owed a video and not yet released' : tileNote(sources.pipeline)
          }
        />
        <Card
          label="Credits spent today"
          value={spend === null ? <span className="neg">?</span> : spend.credits}
          tone={spend === null ? 'bad' : undefined}
          note={
            spend === null
              ? tileNote(sources.ledger)
              : spend.waivedUsd > 0
                ? `plus ${money(spend.waivedUsd)} waived on unlimited accounts`
                : 'holds recorded in the ledger since midnight'
          }
        />
      </div>

      <Attention sources={sources} stuck={stuck} stats={stats} onFixed={onFixed} />

      {pipeline?.runningCommissionIds?.length ? (
        <section className="detail">
          <h2>In flight</h2>
          <table>
            <thead>
              <tr>
                <th>Commission</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pipeline.runningCommissionIds.map((id) => (
                <tr key={id}>
                  <td>
                    <Id value={id} chars={12} />
                  </td>
                  <td>
                    <Link to={{ name: 'commission', commissionId: id }}>open</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* This one wrapped across a line break, so a grep for the service
              name walked straight past it while its neighbours were ported. */}
          <p className="muted small">
            The execution name is the commissionId — that is the only join the
            orchestrator offers, and it is the one this console uses everywhere.
          </p>
        </section>
      ) : null}

      <Pool stats={stats} source={sources.stats} onFixed={onFixed} />

      <p className="muted small" style={{ marginTop: 24 }}>
        <b>Nothing here reports whether this account has MFA.</b> Sign-in is Entra
        now, so multi-factor is a Conditional Access policy on the tenant rather
        than a setting on a user pool — which means it could be on or off and
        this console would look identical either way. The old version of this
        line said "the staff pool has no MFA"; that was true of Cognito and is
        now neither true nor false. What is still true is that this console is
        read-only almost everywhere, and that access is one app-role assignment.
      </p>
    </>
  );
}

/** Everything red, in one list, each item linking to the screen that explains
 *  it. A dashboard that shows a number going the wrong way and no route to the
 *  rows behind it is a status page. */
function Attention({
  sources,
  stuck,
  stats,
  onFixed,
}: {
  sources: Sources;
  stuck: StuckRow[] | null;
  stats: Stats | null;
  onFixed: () => void;
}) {
  const sfnError = sources.stuck.ok ? sources.stuck.value.executionsError : null;
  const missingEmbeddings = stats?.assetsMissingEmbedding ?? 0;
  /**
   * THE ROUTE THE REFUSAL ITSELF NAMES, finally wired up.
   *
   * /admin/pipeline/stuck refuses permanently here and its refusal says, in the
   * server's own words, "GET /admin/pipeline/sweep answers the question that
   * matters — which commissions are holding credits for a film nothing is
   * making". The console printed that sentence in four places and had no client
   * for the route, so an operator was told four times over to go somewhere the
   * portal could not take them. The route is deployed — it is registered in
   * orchestrator.py rather than function_app.py's ROUTES table, which is why
   * reading that table says otherwise.
   *
   * `stuck` still wins when it works: it is a superset (it also finds orphaned
   * executions, which the sweep cannot see) and this fallback exists for the
   * lane where it cannot answer, not instead of it.
   */
  const sweep = !sources.stuck.ok && sources.sweep.ok ? sources.sweep.value : null;
  const findings = sweep?.findings ?? [];
  const swept = Boolean(sweep);

  const nothing =
    (sources.stuck.ok || swept) &&
    !stuck?.length &&
    !findings.length &&
    !missingEmbeddings &&
    !sfnError;

  if (nothing) {
    return (
      <div className="notice">
        <b>Nothing needs a human.</b>
        <span className="muted small">
          {swept
            ? // Named, because the two checks rule out different things and an
              // operator should not read the narrower one as the wider one.
              'No commission is holding credits for a film nothing is making, ' +
              'and every asset is embedded. The sweep read the open commissions ' +
              'and asked the orchestrator about each one; it cannot see orphaned ' +
              'runs, because this deployment exposes no execution history.'
            : 'No stuck commissions, no orphaned executions, every asset embedded. ' +
              'This is a completed check, not an empty one.'}
        </span>
      </div>
    );
  }

  return (
    <section className="detail">
      <h2>Needs a human</h2>
      {/* THIS USED TO BE AN EARLY RETURN, and that was a real regression rather
          than bad copy. /admin/pipeline/stuck refuses permanently on this
          deployment, so a failed stuck-check took the whole section with it —
          including EmbeddingFix at the bottom, the console's only working
          mutating control, whose data source (/admin/stats) answers perfectly.
          A screen must not hide a control behind an unrelated endpoint. */}
      {!sources.stuck.ok && !swept && (
        <Failed
          what="the stuck-pipeline check"
          error={sources.stuck.error}
          cause={sources.stuck.cause}
          retry={onFixed}
          unavailableDetail={
            <>
              {PIPELINE_DETAIL} The sweep it points at could not be reached
              either — {sources.sweep.ok ? '' : sources.sweep.error}. Everything
              else on this page comes from the database and is unaffected —
              including the embedding backfill below, if there is one to do.
            </>
          }
        />
      )}
      {swept && <SweepNote sweep={sweep as Sweep} />}
      {swept && findings.length > 0 && <SweepFindings findings={findings} />}
      {sfnError && (
        <div className="notice notice-bad">
          <b>The orchestrator did not answer.</b>
          <span className="mono small">{sfnError}</span>
          <span className="muted small">
            The database half of every count below is still true. The run half
            is unknown — not zero.
          </span>
        </div>
      )}
      {stuck?.length ? (
        <table>
          <thead>
            <tr>
              <th>What</th>
              <th>Commission</th>
              <th>Family</th>
              <th>Credits</th>
              <th>Age</th>
              <th>What to do</th>
            </tr>
          </thead>
          <tbody>
            {stuck.map((row) => (
              <tr key={`${row.kind}-${row.commissionId}`} className="flagged">
                <td>
                  <Pill tone={row.kind === 'long-running' ? 'warn' : 'bad'}>{row.kind}</Pill>
                </td>
                <td>
                  {row.commissionId ? (
                    <Link to={{ name: 'commission', commissionId: row.commissionId }}>
                      <Id value={row.commissionId} chars={10} />
                    </Link>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  {row.familyId ? (
                    <Link to={{ name: 'families', familyId: row.familyId }}>
                      <Id value={row.familyId} />
                    </Link>
                  ) : (
                    <span className="muted">unknown</span>
                  )}
                </td>
                <td className="num">{row.creditsHeld ?? ''}</td>
                <td className="muted nowrap">
                  {row.ageSeconds ? span(row.ageSeconds) : ''}
                </td>
                {/* The action leads and the diagnosis follows it. This column
                    used to be `why` alone, which made a list called "needs a
                    human" into one no human could act on: it said what had
                    happened and never what to do about it. */}
                <td>
                  <span className="small">{row.do ?? row.why}</span>
                  {row.do && <span className="muted small block">{row.why}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {missingEmbeddings > 0 && (
        <EmbeddingFix count={missingEmbeddings} onFixed={onFixed} />
      )}
    </section>
  );
}

/**
 * Where these rows came from, and — the load-bearing half — that nothing has
 * been refunded.
 *
 * orchestrator.py puts it plainly: "An operator who cannot tell a dry run from
 * a real one reads 'would-release' as 'released' and walks away from a family
 * that still has no credits." This console only ever sends the GET, so `applied`
 * is always false here, and saying so is not optional decoration.
 */
function SweepNote({ sweep }: { sweep: Sweep }) {
  const owed = sweep.findings.filter((f) => f.release === 'would-release');
  return (
    <div className={owed.length ? 'notice notice-warn' : 'notice'}>
      <b>
        {owed.length
          ? `${owed.length} commission${owed.length === 1 ? ' is' : 's are'} holding credits for a film nothing is making.`
          : 'The stranded-hold sweep found nothing owed.'}
      </b>
      <span className="muted small">
        From <span className="mono">GET /admin/pipeline/sweep</span> — the route
        the pipeline's own refusal points at, and the one check on this
        deployment that can answer "who has been charged for nothing". It read{' '}
        {sweep.open ?? '?'} open commission{sweep.open === 1 ? '' : 's'} and
        judged the {sweep.candidates ?? '?'} older than{' '}
        {sweep.thresholdSeconds ? span(sweep.thresholdSeconds) : 'the threshold'}.
      </span>
      {/* Read-only, and it has to be unmissable. */}
      <span className="muted small">
        <b>Nothing here has been refunded.</b> This console only ever sends the
        GET, which never releases — POSTing is what does, and the same code runs
        unattended every fifteen minutes, so a row marked{' '}
        <span className="mono">would-release</span> gets its credits back on the
        next pass without anyone pressing anything.
      </span>
      {Boolean(sweep.statusErrors) && (
        <span className="warn small">
          {sweep.statusErrors} commission{sweep.statusErrors === 1 ? '' : 's'}{' '}
          could not be asked about at all, and are reported rather than refunded
          — refunding on a failed query would refund every open commission during
          an outage.
        </span>
      )}
      {sweep.truncated && (
        <span className="warn small">
          The open-commission scan was truncated, so this is a floor rather than
          the whole picture.
        </span>
      )}
    </div>
  );
}

/**
 * The findings table. Same columns as the stuck table above it on purpose —
 * these answer the same operator question and only differ in which source could
 * answer it — plus the one column the stuck check has no equivalent for: whether
 * a POST would move money.
 */
function SweepFindings({ findings }: { findings: SweepFinding[] }) {
  return (
    <table>
      <thead>
        <tr>
          <th>What</th>
          <th>Commission</th>
          <th>Family</th>
          <th>Credits</th>
          <th>Age</th>
          <th>Refund</th>
          <th>What to do</th>
        </tr>
      </thead>
      <tbody>
        {findings.map((row) => {
          const owed = row.release === 'would-release';
          return (
            <tr key={`${row.kind}-${row.commissionId}`} className={owed ? 'flagged' : ''}>
              <td>
                <Pill tone={owed ? 'bad' : 'warn'} title={row.orchestrationStatus ?? undefined}>
                  {row.kind}
                </Pill>
              </td>
              <td>
                {row.commissionId ? (
                  <Link to={{ name: 'commission', commissionId: row.commissionId }}>
                    <Id value={row.commissionId} chars={10} />
                  </Link>
                ) : (
                  <span className="muted">—</span>
                )}
              </td>
              <td>
                {row.familyId ? (
                  <Link to={{ name: 'families', familyId: row.familyId }}>
                    <Id value={row.familyId} />
                  </Link>
                ) : (
                  // A finding with no familyId is the worst kind: nobody to
                  // refund, and invisible to every byFamily index. veo-test-2
                  // is exactly this and is live.
                  <span className="warn">no family on the row</span>
                )}
              </td>
              <td className="num">{row.creditsHeld ?? ''}</td>
              <td className="muted nowrap">
                {/* `null` here is a row with no usable createdAt — reported
                    rather than aged out, which is the whole reason it is a
                    finding. A blank cell would read as "new". */}
                {row.ageSeconds ? span(row.ageSeconds) : <span className="warn">no date</span>}
              </td>
              <td className="small nowrap">
                {owed ? (
                  <Pill tone="warn" title="a POST to the sweep would release these credits; the unattended sweep will do it within fifteen minutes">
                    would release
                  </Pill>
                ) : (
                  <span className="muted">{row.release ?? 'not owed'}</span>
                )}
              </td>
              {/* The action leads and the diagnosis follows, same as the stuck
                  table: a list called "needs a human" that only says what
                  happened is one no human can act on. */}
              <td>
                <span className="small">{row.do ?? row.why}</span>
                {row.do && <span className="muted small block">{row.why}</span>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Pool({
  stats,
  source,
  onFixed,
}: {
  stats: Stats | null;
  source: Settled<Stats>;
  onFixed: () => void;
}) {
  if (!stats) {
    return (
      <Failed
        what="pool statistics"
        error={source.ok ? 'no stats' : source.error}
        cause={source.ok ? null : source.cause}
        retry={onFixed}
      />
    );
  }
  return (
    <>
      <h2 style={{ fontSize: 15, fontWeight: 500, marginTop: 26 }}>The pool</h2>
      <div className="cards">
        <Card label="Families" value={stats.families} note={`${stats.unlimitedFamilies} unlimited`} />
        <Card label="Videos" value={stats.videos} note={`${stats.videosStitched} stitched`} />
        <Card
          label="Generation spend"
          value={money(stats.generationSpendUsd)}
          note="every asset, all time"
        />
        <Card
          label="Reuse rate"
          value={`${Math.round(stats.reuseRate * 100)}%`}
          note={`${stats.segmentsReused}/${stats.segmentsTotal} segments`}
        />
        <Card label="Assets" value={stats.assets} note={`${stats.aliases} aliases`} />
        <Card
          label="Searchable"
          value={stats.assetsEmbedded}
          note={
            stats.assetsMissingEmbedding
              ? `${stats.assetsMissingEmbedding} missing an embedding`
              : 'all embedded'
          }
          tone={stats.assetsMissingEmbedding ? 'warn' : undefined}
        />
      </div>
    </>
  );
}

/**
 * The one mutating control on this screen, and the only one in the console.
 *
 * Kept because the alternative it replaced was a dashboard that named a problem
 * and then told the operator to go and run a shell script. It is idempotent and
 * bounded, so pressing it twice costs nothing.
 *
 * The audit gap is stated rather than hidden: `showling-ledger` is partitioned
 * on `familyId`, so it structurally cannot hold a record of a platform action.
 * This press mutates up to 100 asset rows and leaves nothing behind but a
 * `print()` in the function's logs. That is worth knowing before pressing it.
 */
function EmbeddingFix({ count, onFixed }: { count: number; onFixed: () => void }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  /**
   * The refusal, WITH its cause. This was a bare string in a red span, which on
   * the console's only mutating control meant the whole explanation of a failed
   * press could be the characters "503": api.ts falls back to the status when a
   * body carries no `error`, and nothing here added a word to it. Keeping the
   * cause is what lets `Refused` say which of the four things happened —
   * notably a `staff` role removed mid-session, which is not a fault at all.
   */
  const [refused, setRefused] = useState<{ error: string; cause: unknown } | null>(null);

  const run = async () => {
    setBusy(true);
    setRefused(null);
    try {
      const out = await backfillEmbeddings();
      setNote(
        `Embedded ${out.embedded}.` +
          (out.remaining ? ` ${out.remaining} still to do — run it again.` : ' All done.'),
      );
      onFixed();
    } catch (err) {
      setRefused({ error: message(err), cause: err });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="notice notice-warn">
      <b>
        {count} asset{count === 1 ? ' has' : 's have'} no embedding.
      </b>
      {/* Lead with the recommendation. The caveats are real and stay, but a
          notice whose first three lines are all caveat reads as a warning not
          to press the button, when pressing it is in fact the right move. */}
      <span className="small">
        Press the button. Embedding is cheap, it is safe to re-run, and nothing
        breaks if you leave it — you simply keep paying to regenerate lookalikes
        the pool already has.
      </span>
      <span className="muted small">
        Nearest-neighbour search cannot find them, so similar requests regenerate
        them instead of reusing them — that is real provider money, once per miss.
        The press writes no audit row: the ledger is partitioned on familyId and
        cannot record a platform action, so the only trace is a log line.
      </span>
      <button onClick={() => void run()} disabled={busy}>
        {busy ? 'embedding…' : `Embed them now`}
      </button>
      {note && <span className="ok small">{note}</span>}
      {refused && (
        <Refused what="the embedding backfill" error={refused.error} cause={refused.cause} />
      )}
    </div>
  );
}

/* --- small maths ---------------------------------------------------------- */

function fallback(value: number | undefined | null) {
  return value === undefined || value === null ? <span className="muted">—</span> : value;
}

function withinDays(iso: string | undefined, days: number): boolean {
  if (!iso) return true; // no date is not evidence of being old; show it
  const at = Date.parse(iso);
  return Number.isNaN(at) ? true : Date.now() - at < days * 86_400_000;
}

/**
 * Credits committed since local midnight.
 *
 * Holds, not grants: a grant is us giving credits away, a hold is a family
 * spending them, and only the second is "spent today". Waived rows are counted
 * separately in dollars — they are the unlimited accounts, where no credits
 * moved but real provider money did, and folding them together is how a
 * dashboard starts counting internal testing as revenue.
 */
function spentToday(entries: LedgerRow[]): { credits: number; waivedUsd: number } {
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  let credits = 0;
  let waivedUsd = 0;
  for (const entry of entries) {
    const at = entry.at ? Date.parse(entry.at) : NaN;
    if (Number.isNaN(at) || at < midnight.getTime()) continue;
    if (entry.waived) waivedUsd += entry.wouldHaveCost ?? 0;
    else if (entry.type === 'hold') credits += Math.abs(entry.credits);
  }
  return { credits, waivedUsd: Math.round(waivedUsd * 10000) / 10000 };
}

